import React from 'react';
import { Database, KeyRound, Plug, Server } from 'lucide-react';
import DashboardNav, { DashboardBrandRow } from './DashboardNav';
import { mcpApiUrl } from './mcpApi';

export default function McpAccessDashboard() {
  const catalogExample = mcpApiUrl('/v1/tools/catalog?include_details=false&hierarchy=false');
  const hubExample = mcpApiUrl('/v1/account-hub/apps');

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 p-6 font-mono">
      <div className="max-w-4xl mx-auto space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 border-b border-slate-800 pb-4">
          <DashboardBrandRow />
          <DashboardNav />
        </div>

        <div className="rounded-xl border border-slate-800 bg-slate-900 p-6 space-y-4">
          <p className="text-[10px] tracking-[0.2em] text-slate-500 uppercase">Model Context Protocol</p>
          <h2 className="text-lg font-bold">MCP access</h2>
          <p className="text-sm text-slate-400">
            Connect an MCP client (Cursor, Claude Desktop, custom runners) to this engine. The HTTP catalog exposes tool metadata and
            account-hub control routes for dashboards and integrations.
          </p>
        </div>

        <div className="grid gap-4 md:grid-cols-2">
          <div className="rounded-xl border border-slate-800 bg-slate-900 p-4">
            <div className="flex items-center gap-2 text-cyan-400 mb-2">
              <Plug size={16} />
              <h3 className="text-xs font-bold uppercase text-slate-300">stdio MCP server</h3>
            </div>
            <p className="text-xs text-slate-400 mb-2">
              Run the bundled MCP server with <code className="text-cyan-200">MCP_TRANSPORT=stdio</code> (default). Your client
              launches the binary and speaks JSON-RPC over stdin/stdout.
            </p>
          </div>
          <div className="rounded-xl border border-slate-800 bg-slate-900 p-4">
            <div className="flex items-center gap-2 text-amber-400 mb-2">
              <Server size={16} />
              <h3 className="text-xs font-bold uppercase text-slate-300">HTTP tool catalog</h3>
            </div>
            <p className="text-xs text-slate-400 mb-2">
              Tool listing (relative URL works with <code className="text-cyan-200">npm start</code> proxy):
            </p>
            <code className="block text-[10px] text-emerald-300 break-all bg-slate-950 p-2 rounded border border-slate-800">
              {catalogExample}
            </code>
          </div>
          <div className="rounded-xl border border-slate-800 bg-slate-900 p-4">
            <div className="flex items-center gap-2 text-violet-400 mb-2">
              <KeyRound size={16} />
              <h3 className="text-xs font-bold uppercase text-slate-300">API key</h3>
            </div>
            <p className="text-xs text-slate-400">
              When <code className="text-cyan-200">MCP_HTTP_API_KEY</code> is set, send header{' '}
              <code className="text-cyan-200">X-API-KEY</code>. Mirror it in the dashboard via{' '}
              <code className="text-cyan-200">REACT_APP_MCP_HTTP_API_KEY</code>.
            </p>
          </div>
          <div className="rounded-xl border border-slate-800 bg-slate-900 p-4">
            <div className="flex items-center gap-2 text-emerald-400 mb-2">
              <Database size={16} />
              <h3 className="text-xs font-bold uppercase text-slate-300">Account Hub API</h3>
            </div>
            <p className="text-xs text-slate-400 mb-2">Example (apps list):</p>
            <code className="block text-[10px] text-emerald-300 break-all bg-slate-950 p-2 rounded border border-slate-800">
              {hubExample}
            </code>
            <p className="text-xs text-slate-500 mt-2">
              Create app tokens and toggle transactional scopes under{' '}
              <a href="/account-hub/apps" className="text-emerald-400 hover:underline">
                Account Hub → App integrations
              </a>
              .
            </p>
          </div>
        </div>

        <p className="text-[10px] text-slate-600">
          Static hosting: set <code className="text-slate-400">REACT_APP_MCP_API_BASE</code> to your MCP HTTP origin (for example{' '}
          <code className="text-slate-400">http://127.0.0.1:8082</code>) so API calls do not hit the SPA shell.
        </p>
      </div>
    </div>
  );
}
